import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useLoaderData } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader
} from '@material-tailwind/react'

import { attachmentApi } from '@/store/services/attachment.service'
import { LinkWithQuery } from '@/shared/components/link-with-query'
import { Pill } from '@/shared/components/pill'
import { AttachmentTableWithTabs } from '@/shared/components/attachments'
import { store } from '@/store'
import { useAuthorization } from '@/hooks'
import { HasAccess } from '@permify/react-role'

import { ZipDownloadRangeDialog } from '@/shared/components/zip-download-range-dialog'
import { useCreateZipAttachmentMutation } from '@/store/services/zip-attachment.service'

export const CompanyAttachments = () => {
  const { company: currentCompany } = useLoaderData()
  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })

  const { t } = useTranslation()

  const defaultSearchParams = {
    attachable_type: 'Contractor::Company',
    attachable_id: currentCompany.id
  }

  const pillOptions = useMemo(() => {
    return [
      { label: t('company.name'), value: currentCompany.name },
      { label: t('documentNumber.one'), value: currentCompany.documentNumber }
    ]
  }, [currentCompany])

  const getMore = async (searchParams) => {
    try {
      const {
        attachments = [], meta = {}
      } = await store
        .dispatch(
          attachmentApi.endpoints.getAttachments.initiate(
            `?${searchParams.toString()}`
          )
        )
        .unwrap()

      const currentPage = Number(searchParams.get('page'))
      const hasMore = currentPage < meta?.pagination?.total_pages

      return {
        attachments,
        hasMore
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  useEffect(() => {
    hasAccess('company#attachments')
  }, [])

  // ---- ZIP: state and logic ----
  const [isZipDialogOpen, setIsZipDialogOpen] = useState(false)
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false)
  const [existingExportInfo, setExistingExportInfo] = useState(null)
  const [pendingZipRequest, setPendingZipRequest] = useState(null)

  const [createZipAttachment, { isLoading: isCreatingZip }] =
    useCreateZipAttachmentMutation()

  const requestZip = async ({ fromDate, toDate }, { force = false } = {}) => {
    try {
      const response = await createZipAttachment({
        attachable_type: 'Contractor::Company',
        attachable_id: currentCompany.id,
        from_date: fromDate,
        to_date: toDate,
        ...(force ? { force: true } : {})
      }).unwrap()

      if (response?.conflict && response?.existing_export && !force) {
        setExistingExportInfo(response.existing_export)
        setPendingZipRequest({ fromDate, toDate })
        setIsConflictDialogOpen(true)
        return
      }

      // Success: close all related dialogs
      setIsZipDialogOpen(false)
      setIsConflictDialogOpen(false)
      setExistingExportInfo(null)
      setPendingZipRequest(null)

    } catch (error) {
      //console.error('Error requesting company ZIP', error)

      // Keep other errors with the existing behavior
    }
  }

  const handleZipConfirm = ({ fromDate, toDate }) => {
    requestZip({ fromDate, toDate }, { force: false })
  }

  const handleConflictCancel = () => {
    setIsConflictDialogOpen(false)
    setExistingExportInfo(null)
    setPendingZipRequest(null)
  }

  const handleConflictConfirm = () => {
    if (!pendingZipRequest) return
    requestZip(pendingZipRequest, { force: true })
  }

  return (
    <Dialog size='xl' className='h-4/5' open={true}>
      <DialogHeader>Detalle de documentos subidos</DialogHeader>

      <DialogBody className='overflow-hidden' style={{ height: 'inherit' }}>
        <div className='flex gap-3 mb-5'>
          {!!pillOptions.length &&
            pillOptions.map(({ value, label }) => (
              <Pill key={value} label={label} value={value} />
            ))}
        </div>

        <AttachmentTableWithTabs
          initialSearchParams={defaultSearchParams}
          getMore={getMore}
        />
      </DialogBody>

      <DialogFooter className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        {/* Section: button to open the date-range dialog */}
        <div className='flex flex-col items-start gap-1'>
          <HasAccess permissions="attachment#download_batch">
            <Button
              variant='outlined'
              onClick={() => setIsZipDialogOpen(true)}
              disabled={isCreatingZip}
            >
              Descargar documentos por período
            </Button>
          </HasAccess>
        </div>

        {/* Existing buttons */}
        <div className='flex gap-3'>
          <LinkWithQuery to='/companies'>
            <Button
              variant='outlined'
              color='white'
            >
              Volver
            </Button>
          </LinkWithQuery>

          <LinkWithQuery
            to={generatePath('/companies/:id/attachments/upload', {
              id: currentCompany.id
            })}
          >
            <Button>
              Añadir documento
            </Button>
          </LinkWithQuery>
        </div>

        {/* Reusable dialog for date range + conflict */}
        <ZipDownloadRangeDialog
          open={isZipDialogOpen}
          onClose={() => setIsZipDialogOpen(false)}
          onConfirm={handleZipConfirm}
          isLoading={isCreatingZip}
          title='Descargar documentos del contratista'
          conflict={{
            open: isConflictDialogOpen,
            existingExport: existingExportInfo,
            isLoading: isCreatingZip,
            onCancel: handleConflictCancel,
            onConfirm: handleConflictConfirm
          }}
        />
      </DialogFooter>
    </Dialog>
  )
}
